import type { ChartDataInterface, ChartStatisticInterface } from 'src/types/Chart';
import type { AvatarColorVariantTypes } from '@Shelly/shelly-design-system';
import type { EnergyMonitoringStats } from './store';
import type { RoomChartModel, RoomListEntry } from './energy_room_charts_helpers';
import type { PhaseHealthSummary } from './energy_phase_helpers';
import type { PeakDevicePanel } from './energy_peak_helpers';
import type { TariffBucket } from './energy_tariff_helpers';
import { CONSUMER_MIX_COLORS } from './energy_demo_data';

const HOUR_MS = 60 * 60 * 1000;

const buildDemoHourlyPoints = (hours: number, profile: (index: number) => number) => {
	const end = Date.now();
	return Array.from({ length: hours }, (_, index) => ({
		dt: end - (hours - 1 - index) * HOUR_MS,
		value: Math.round(profile(index)),
		cost: Math.round(profile(index) * 0.00018 * 100) / 100,
	}));
};

export const DEMO_PHASE_HEALTH: PhaseHealthSummary = {
	phases: [
		{
			phase: 'A',
			displayName: 'Phase A',
			power: 2140,
			label: '2.14 kW',
			percent: 88,
			tone: 'primary',
		},
		{
			phase: 'B',
			displayName: 'Phase B',
			power: 1890,
			label: '1.89 kW',
			percent: 78,
			tone: 'success',
		},
		{
			phase: 'C',
			displayName: 'Phase C',
			power: 2430,
			label: '2.43 kW',
			percent: 100,
			tone: 'warning',
		},
	],
	totalLabel: '6.42 kW',
	imbalancePercent: 12,
	isBalanced: true,
	sourceDeviceName: 'Main 3EM',
};

export const DEMO_PEAK_PANELS: PeakDevicePanel[] = [
	{
		deviceId: 'demo-peak',
		deviceName: 'Main 3EM',
		isPreferred: true,
		isThreePhase: true,
		phases: [
			{
				channel: 0,
				label: 'Phase A',
				live: {
					value: '7.8 kW',
					time: '18:42',
					timestamp: 0,
					watts: 7800,
					consumedWatts: 7800,
					returnedWatts: 0,
					direction: 'import',
				},
				avg5: {
					value: '6.1 kW',
					time: '18:37',
					timestamp: 0,
					watts: 6100,
					consumedWatts: 6100,
					returnedWatts: 0,
					direction: 'import',
				},
				liveWatts: 7800,
				avg5Watts: 6100,
			},
			{
				channel: 1,
				label: 'Phase B',
				live: {
					value: '6.9 kW',
					time: '18:40',
					timestamp: 0,
					watts: 6900,
					consumedWatts: 6900,
					returnedWatts: 0,
					direction: 'import',
				},
				avg5: {
					value: '5.4 kW',
					time: '18:35',
					timestamp: 0,
					watts: 5400,
					consumedWatts: 5400,
					returnedWatts: 0,
					direction: 'import',
				},
				liveWatts: 6900,
				avg5Watts: 5400,
			},
			{
				channel: 2,
				label: 'Phase C',
				live: {
					value: '7.2 kW',
					time: '18:41',
					timestamp: 0,
					watts: 7200,
					consumedWatts: 7200,
					returnedWatts: 0,
					direction: 'import',
				},
				avg5: {
					value: '5.8 kW',
					time: '18:36',
					timestamp: 0,
					watts: 5800,
					consumedWatts: 5800,
					returnedWatts: 0,
					direction: 'import',
				},
				liveWatts: 7200,
				avg5Watts: 5800,
			},
		],
	},
];

const DEMO_ROOM_CHARTS: Record<
	string,
	{
		totalWh: number;
		devices: Array<{ title: string; totalWh: number }>;
	}
> = {
	'living room': {
		totalWh: 1180,
		devices: [
			{ title: 'Heating & cooling', totalWh: 420 },
			{ title: 'Living room lights', totalWh: 310 },
			{ title: 'Media center', totalWh: 280 },
			{ title: 'Standby group', totalWh: 170 },
		],
	},
	kitchen: {
		totalWh: 3600,
		devices: [
			{ title: 'Oven', totalWh: 1420 },
			{ title: 'Fridge', totalWh: 980 },
			{ title: 'Dishwasher', totalWh: 760 },
			{ title: 'Counter plugs', totalWh: 440 },
		],
	},
	garage: {
		totalWh: 6200,
		devices: [
			{ title: 'EV charger', totalWh: 4200 },
			{ title: 'Workshop', totalWh: 1200 },
			{ title: 'Garage door', totalWh: 800 },
		],
	},
	office: {
		totalWh: 1100,
		devices: [
			{ title: 'Desktop', totalWh: 520 },
			{ title: 'Monitors', totalWh: 340 },
			{ title: 'Network rack', totalWh: 240 },
		],
	},
};

const resolveDemoRoomProfile = (roomName: string) => {
	const key = roomName.trim().toLowerCase();
	return (
		DEMO_ROOM_CHARTS[key] ?? {
			totalWh: 1860,
			devices: [
				{ title: 'Primary load', totalWh: 920 },
				{ title: 'Secondary load', totalWh: 640 },
				{ title: 'Standby', totalWh: 300 },
			],
		}
	);
};

const buildDemoGenerationChartData = (): ChartDataInterface => {
	const consumption = buildDemoHourlyPoints(24, (index) => {
		const eveningPeak = index >= 17 && index <= 21 ? 1.45 : 1;
		const morning = index >= 6 && index <= 9 ? 1.2 : 1;
		return 820 * eveningPeak * morning * (0.82 + Math.sin(index / 2.8) * 0.18);
	});
	const generated = buildDemoHourlyPoints(24, (index) => {
		const daylight = index >= 8 && index <= 17 ? 1 : 0.15;
		return 640 * daylight * (0.75 + Math.cos(index / 3.1) * 0.2);
	});

	return {
		id: 'demo-generation',
		name: 'pv_generation',
		type: 'areaspline',
		units: 'Wh',
		height: 196,
		rangePeriod: '24_hours',
		rangeInterval: 'hour',
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		series: [
			{
				name: 'total_house_energy',
				style: 'overall_consumption',
				visible: true,
				data: consumption,
			},
			{
				name: 'generated_energy',
				style: 'generated_energy',
				visible: true,
				data: generated,
			},
		],
	};
};

const buildDemoStatistic = (): ChartStatisticInterface => ({
	available: true,
	totalConsumed: '23.7 kWh',
	totalConsumedValue: 23700,
	totalGenerated: '12.9 kWh',
	totalGeneratedValue: 12900,
	totalCost: 'EUR 4.86',
});

const buildDemoTariffBuckets = (): TariffBucket[] => [
	{
		id: 'off-peak',
		name: 'Off-peak',
		valueWh: 16200,
		cost: 2.86,
		price: 0.00012,
		color: '--color-chart-money-value',
	},
	{
		id: 'peak',
		name: 'Peak',
		valueWh: 7500,
		cost: 2.0,
		price: 0.00027,
		color: '--color-chart-consumed-energy',
	},
];

export const buildDemoEnergyMonitoringStats = (): EnergyMonitoringStats => ({
	loading: false,
	error: false,
	chartData: buildDemoGenerationChartData(),
	statistic: buildDemoStatistic(),
	tariffBuckets: buildDemoTariffBuckets(),
});

export const buildDemoRoomChartModel = (room: RoomListEntry): RoomChartModel => {
	const profile = resolveDemoRoomProfile(room.name);
	const points = buildDemoHourlyPoints(24, (index) => {
		const factor = 0.85 + Math.sin(index / 2.5) * 0.15;
		return (profile.totalWh / 24) * factor;
	});

	return {
		id: String(room.id),
		name: room.name,
		icon: room.icon ?? 'fa-light fa-door-open',
		color: (room.backgroundColor ?? 'color-primary') as AvatarColorVariantTypes,
		totalLabel: `${(profile.totalWh / 1000).toFixed(2)} kWh`,
		reportedCost: profile.totalWh * 0.0002,
		chartData: {
			id: `demo-room-${room.id}`,
			name: `room-${room.id}`,
			type: 'bar',
			units: 'Wh',
			height: 160,
			rangePeriod: '24_hours',
			rangeInterval: 'hour',
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			thumbMode: true,
			seriesLabels: Object.fromEntries(
				profile.devices.map((device) => [device.title, device.title]),
			),
			series: profile.devices.map((device, index) => ({
				name: device.title,
				style: `room_device_${index}`,
				visible: true,
				data: buildDemoHourlyPoints(24, (hourIndex) => {
					const share = device.totalWh / profile.totalWh;
					const factor = 0.85 + Math.sin((hourIndex + index) / 2.3) * 0.15;
					return (profile.totalWh / 24) * share * factor;
				}),
			})),
		} as RoomChartModel['chartData'],
		devices: profile.devices.map((device, index) => ({
			id: `demo-${room.id}-${index}`,
			seriesKey: device.title,
			title: device.title,
			valueLabel: `${device.totalWh} Wh`,
			totalWh: device.totalWh,
			color: CONSUMER_MIX_COLORS[index % CONSUMER_MIX_COLORS.length],
		})),
	};
};
